import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BirthdayToday } from "@/components/birthdays/BirthdayToday";
import { BirthdayUpcoming } from "@/components/birthdays/BirthdayUpcoming";
import { BirthdayHistory } from "@/components/birthdays/BirthdayHistory";
import { BirthdaySettings } from "@/components/birthdays/BirthdaySettings";
import { ShieldCheck } from "lucide-react";

const Stat = ({ label, value, testid, tone = "text-[#0B1F44]" }) => (
  <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
    <p data-testid={testid} className={`mt-1 text-2xl font-bold ${tone}`}>{value ?? "—"}</p>
  </div>
);

export default function Birthdays() {
  const { data } = useQuery({ queryKey: ["birthdays", "overview"], queryFn: async () => (await api.get("/birthdays/overview")).data, refetchInterval: 30000 });
  const s = data?.stats || {};

  return (
    <Layout title="Patient Birthday Greetings" subtitle="One friendly greeting per patient per year, by text or email">
      {data && !data.settings.enabled && (
        <p data-testid="bday-off-banner" className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800">
          Automatic greetings are <b>off</b>. Check the templates and settings, then switch them on in Settings & templates.</p>)}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7">
        <Stat label="Birthdays today" value={s.birthdays_today} testid="bday-stat-today" />
        <Stat label="Next 7 days" value={s.upcoming_7_days} testid="bday-stat-upcoming" />
        <Stat label="Scheduled today" value={s.scheduled_today} testid="bday-stat-scheduled" tone="text-sky-700" />
        <Stat label="Sent this year" value={s.sent} testid="bday-stat-sent" tone="text-emerald-700" />
        <Stat label="Failed / uncertain" value={s.failed_or_uncertain} testid="bday-stat-problems" tone={s.failed_or_uncertain ? "text-red-600" : "text-[#0B1F44]"} />
        <Stat label="Skipped" value={s.skipped} testid="bday-stat-skipped" tone="text-slate-600" />
        <Stat label="Duplicates prevented" value={s.duplicates_prevented} testid="bday-stat-dups" tone="text-violet-700" />
      </div>
      <p className="mt-2 flex items-center gap-1.5 text-xs text-slate-500"><ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
        Greetings never mention treatment or medicines. Opted-out patients are always skipped. {s.patients_with_birthday ?? 0} patients have a birthday on file (from DrChrono).</p>
      {data && (
        <Tabs defaultValue="today" className="mt-6">
          <TabsList className="flex h-auto flex-wrap justify-start">
            <TabsTrigger value="today" data-testid="bday-tab-today">Today{s.awaiting_approval ? ` (${s.awaiting_approval} to approve)` : ""}</TabsTrigger>
            <TabsTrigger value="upcoming" data-testid="bday-tab-upcoming">Next 7 days</TabsTrigger>
            <TabsTrigger value="history" data-testid="bday-tab-history">History</TabsTrigger>
            <TabsTrigger value="settings" data-testid="bday-tab-settings">Settings & templates</TabsTrigger>
          </TabsList>
          <TabsContent value="today" className="mt-4"><BirthdayToday data={data} /></TabsContent>
          <TabsContent value="upcoming" className="mt-4"><BirthdayUpcoming rows={data.upcoming} mode={data.settings.channel_mode} /></TabsContent>
          <TabsContent value="history" className="mt-4"><BirthdayHistory /></TabsContent>
          <TabsContent value="settings" className="mt-4"><BirthdaySettings initial={data.settings} /></TabsContent>
        </Tabs>
      )}
    </Layout>
  );
}
